import type { Property } from '@/types/api';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

const STORAGE_KEY = 'manejai_property_id';

interface PropertyState {
  propertyId: number | null;
  setPropertyId: (id: number | null) => void;
  selectFromList: (properties: Property[]) => void;
}

const PropertyContext = createContext<PropertyState | null>(null);

export function PropertyProvider({ children }: { children: ReactNode }) {
  const [propertyId, setPropertyIdState] = useState<number | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? Number(stored) : null;
  });

  const setPropertyId = useCallback((id: number | null) => {
    setPropertyIdState(id);
    if (id) localStorage.setItem(STORAGE_KEY, String(id));
    else localStorage.removeItem(STORAGE_KEY);
  }, []);

  const selectFromList = useCallback(
    (properties: Property[]) => {
      if (properties.length === 0) {
        if (propertyId !== null) setPropertyId(null);
        return;
      }

      const stillValid = propertyId != null && properties.some((p) => p.id === propertyId);
      if (stillValid) return;

      // ID antigo (ex.: trocou de conta) ou nenhuma seleção → usa a primeira fazenda.
      setPropertyId(properties[0].id);
    },
    [propertyId, setPropertyId],
  );

  useEffect(() => {
    if (propertyId) localStorage.setItem(STORAGE_KEY, String(propertyId));
  }, [propertyId]);

  const value = useMemo(
    () => ({ propertyId, setPropertyId, selectFromList }),
    [propertyId, setPropertyId, selectFromList],
  );

  return <PropertyContext.Provider value={value}>{children}</PropertyContext.Provider>;
}

export function usePropertyScope() {
  const ctx = useContext(PropertyContext);
  if (!ctx) throw new Error('usePropertyScope must be used within PropertyProvider');
  return ctx;
}
